import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, PhoneOff, RotateCcw, ArrowDown, ArrowUp, Info, CheckCircle2 } from 'lucide-react';
type Step = 'idle' | 'dialing' | 'connecting' | 'menu' | 'account' | 'confirm' | 'pin' | 'processing' | 'success' | 'cancelled';
interface Store {
  name: string;
  balance: number;
  accountEnding: string;
}
const STORES: Store[] = [{
  name: 'Truworths',
  balance: 1240.0,
  accountEnding: '8821'
}, {
  name: 'Edgars',
  balance: 487.5,
  accountEnding: '4421'
}, {
  name: 'Mr Price',
  balance: 215.75,
  accountEnding: '6633'
}, {
  name: 'Foschini',
  balance: 962.4,
  accountEnding: '1109'
}, {
  name: 'Woolworths',
  balance: 0.0,
  accountEnding: '7754'
}];
const STEP_HINTS: Record<Step, {
  title: string;
  body: string;
}> = {
  idle: {
    title: 'Ready to start',
    body: "On a real phone, you would dial *120*Masube# (that's *120*237799#) and press the green Call button. Try it — or just press Call to skip ahead."
  },
  dialing: {
    title: 'Dialing in...',
    body: 'Type the rest of the number using the keypad, then press the green Call button to connect.'
  },
  connecting: {
    title: 'Connecting...',
    body: 'Your phone is reaching Masube Settle. This uses your network, not data — so it works on any phone, anywhere.'
  },
  menu: {
    title: 'Pick a store',
    body: 'Press the number next to the store you want to pay. For this demo, try pressing 2 to pay Edgars.'
  },
  account: {
    title: 'Your balance',
    body: 'We show you the account balance directly from the store. Press 1 to pay the full amount, or 2 to enter a different amount.'
  },
  confirm: {
    title: 'Please confirm',
    body: "You're about to send money via PayShap — a safe, instant bank transfer. Press 1 to confirm, or 2 to cancel."
  },
  pin: {
    title: 'Enter your PIN',
    body: 'Type your 4-digit Masube PIN (try 1234 for this demo), then press # to submit. Your PIN never leaves your phone.'
  },
  processing: {
    title: 'Processing securely',
    body: 'Masube is talking to your bank using PayShap. This usually takes a few seconds.'
  },
  success: {
    title: 'Done!',
    body: "Your payment is complete. You'll get an SMS receipt with your safe word so you know it's really us."
  },
  cancelled: {
    title: 'Cancelled',
    body: "No payment was made. You can start again whenever you're ready."
  }
};
export function UssdSimulator() {
  const [step, setStep] = useState<Step>('idle');
  const [dialString, setDialString] = useState('');
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [pin, setPin] = useState('');
  const [pressedKey, setPressedKey] = useState<string | null>(null);
  const screenRef = useRef<HTMLDivElement>(null);
  // Auto-advance for transient states
  useEffect(() => {
    if (step === 'connecting') {
      const t = setTimeout(() => setStep('menu'), 1400);
      return () => clearTimeout(t);
    }
    if (step === 'processing') {
      const t = setTimeout(() => setStep('success'), 1800);
      return () => clearTimeout(t);
    }
  }, [step]);
  const reset = () => {
    setStep('idle');
    setDialString('');
    setSelectedStore(null);
    setPin('');
  };
  const handleCall = () => {
    if (step === 'idle' || step === 'dialing') {
      // Accept anything starting with *120* or empty (shortcut)
      if (dialString === '' || /^\*120\*?.*#?$/.test(dialString)) {
        setStep('connecting');
        setDialString('');
      } else {
        // simulate "no service"
        setStep('connecting');
        setDialString('');
      }
    }
  };
  const handleEnd = () => {
    if (step !== 'idle') {
      reset();
    }
  };
  const handleKey = (key: string) => {
    setPressedKey(key);
    setTimeout(() => setPressedKey(null), 120);
    // From idle, any keypress starts dialing mode
    if (step === 'idle') {
      setStep('dialing');
      setDialString(key);
      return;
    }
    if (step === 'dialing') {
      setDialString(d => d.length < 20 ? d + key : d);
      // Auto-submit on # if dial string looks like USSD
      if (key === '#' && /^\*120\*/.test(dialString + key)) {
        setTimeout(() => {
          setStep('connecting');
          setDialString('');
        }, 200);
      }
      return;
    }
    if (step === 'menu') {
      const idx = parseInt(key, 10);
      if (idx >= 1 && idx <= STORES.length) {
        setSelectedStore(STORES[idx - 1]);
        setStep('account');
      } else if (key === '0') {

        // help — just stay
      } else if (key === '9') {
        setStep('cancelled');
      }
      return;
    }
    if (step === 'account') {
      if (key === '1') {
        setStep('confirm');
      } else if (key === '2') {
        // For demo, treat same as 1
        setStep('confirm');
      } else if (key === '9') {
        setStep('menu');
      }
      return;
    }
    if (step === 'confirm') {
      if (key === '1') setStep('pin');else if (key === '2') setStep('cancelled');
      return;
    }
    if (step === 'pin') {
      if (key === '#') {
        if (pin.length >= 4) {
          setStep('processing');
          setPin('');
        }
      } else if (key === '*') {
        setPin(p => p.slice(0, -1));
      } else if (/^\d$/.test(key) && pin.length < 4) {
        setPin(p => p + key);
      }
      return;
    }
    if (step === 'success' || step === 'cancelled') {
      reset();
      return;
    }
  };
  // ===== Screen content per step =====
  const renderScreen = () => {
    switch (step) {
      case 'idle':
        return <div className="font-mono text-[13px] leading-snug" data-id="element-422">
            <div className="text-[11px] opacity-60 mb-2" data-id="element-423">MTN-SA • 09:41</div>
            <div className="border-b border-black/20 pb-1 mb-2 font-bold" data-id="element-424">
              Ready
            </div>
            <div className="opacity-80" data-id="element-425">Dial a number, then press</div>
            <div className="opacity-80" data-id="element-426">the green Call button.</div>
            <div className="mt-4 text-center font-bold text-[15px]" data-id="element-427">_</div>
            <div className="mt-6 text-[10px] opacity-60 text-center" data-id="element-428">
              Try: *120*23779#
            </div>
          </div>;
      case 'dialing':
        return <div className="font-mono text-[13px] leading-snug" data-id="element-429">
            <div className="text-[11px] opacity-60 mb-2" data-id="element-430">MTN-SA • 09:41</div>
            <div className="border-b border-black/20 pb-1 mb-2 font-bold" data-id="element-431">
              Dial number
            </div>
            <div className="mt-6 text-center font-bold text-xl tracking-wider break-all" data-id="element-432">
              {dialString}
              <span className="animate-pulse" data-id="element-433">_</span>
            </div>
            <div className="mt-8 text-[10px] opacity-60 text-center" data-id="element-434">
              Press <span className="font-bold" data-id="element-435">Call</span> to dial
            </div>
          </div>;
      case 'connecting':
        return <div className="font-mono text-[13px] leading-snug" data-id="element-436">
            <div className="text-[11px] opacity-60 mb-2" data-id="element-437">USSD</div>
            <div className="mt-10 text-center" data-id="element-438">
              <div className="font-bold text-[15px] mb-2" data-id="element-439">Connecting</div>
              <div className="flex justify-center gap-1" data-id="element-440">
                <span className="animate-pulse" data-id="element-441">.</span>
                <span className="animate-pulse" style={{
                animationDelay: '0.2s'
              }} data-id="element-442">
                  
                  .
                </span>
                <span className="animate-pulse" style={{
                animationDelay: '0.4s'
              }} data-id="element-443">
                  
                  .
                </span>
              </div>
            </div>
          </div>;
      case 'menu':
        return <div className="font-mono text-[13px] leading-snug" data-id="element-444">
            <div className="text-[11px] opacity-60 mb-1" data-id="element-445">USSD</div>
            <div className="font-bold border-b border-black/20 pb-1 mb-2" data-id="element-446">
              Masube Settle
            </div>
            <div className="space-y-0.5" data-id="element-447">
              {STORES.map((s, i) => <div key={s.name} data-id="element-448">
                  {i + 1}. {s.name}
                </div>)}
              <div className="pt-1 opacity-70" data-id="element-449">0. Help</div>
              <div className="opacity-70" data-id="element-450">9. Cancel</div>
            </div>
            <div className="mt-2 border-t border-black/20 pt-1" data-id="element-451">Reply: _</div>
          </div>;
      case 'account':
        if (!selectedStore) return null;
        return <div className="font-mono text-[13px] leading-snug" data-id="element-452">
            <div className="text-[11px] opacity-60 mb-1" data-id="element-453">USSD</div>
            <div className="font-bold border-b border-black/20 pb-1 mb-2" data-id="element-454">
              {selectedStore.name}
            </div>
            <div data-id="element-455">Account •••{selectedStore.accountEnding}</div>
            <div className="mt-2" data-id="element-456">Balance:</div>
            <div className="font-bold text-[15px]" data-id="element-457">
              R {selectedStore.balance.toFixed(2)}
            </div>
            <div className="mt-3 space-y-0.5" data-id="element-458">
              <div data-id="element-459">1. Pay full amount</div>
              <div data-id="element-460">2. Enter amount</div>
              <div className="opacity-70" data-id="element-461">9. Back</div>
            </div>
            <div className="mt-2 border-t border-black/20 pt-1" data-id="element-462">Reply: _</div>
          </div>;
      case 'confirm':
        if (!selectedStore) return null;
        return <div className="font-mono text-[13px] leading-snug" data-id="element-463">
            <div className="text-[11px] opacity-60 mb-1" data-id="element-464">USSD</div>
            <div className="font-bold border-b border-black/20 pb-1 mb-2" data-id="element-465">
              Confirm payment
            </div>
            <div data-id="element-466">Pay R {selectedStore.balance.toFixed(2)}</div>
            <div data-id="element-467">to {selectedStore.name}</div>
            <div data-id="element-468">via PayShap?</div>
            <div className="mt-3 space-y-0.5" data-id="element-469">
              <div data-id="element-470">1. Yes, pay now</div>
              <div data-id="element-471">2. No, cancel</div>
            </div>
            <div className="mt-2 border-t border-black/20 pt-1" data-id="element-472">Reply: _</div>
          </div>;
      case 'pin':
        return <div className="font-mono text-[13px] leading-snug" data-id="element-473">
            <div className="text-[11px] opacity-60 mb-1" data-id="element-474">USSD • Secure</div>
            <div className="font-bold border-b border-black/20 pb-1 mb-2" data-id="element-475">
              Enter Masube PIN
            </div>
            <div className="opacity-80" data-id="element-476">4 digits. Press # to send.</div>
            <div className="mt-6 text-center font-bold text-2xl tracking-[0.5em]" data-id="element-477">
              {'•'.repeat(pin.length)}
              {pin.length < 4 && <span className="opacity-30" data-id="element-478">{'_'.repeat(4 - pin.length)}</span>}
            </div>
            <div className="mt-4 text-[10px] opacity-60 text-center" data-id="element-479">
              * to delete • # to submit
            </div>
          </div>;
      case 'processing':
        return <div className="font-mono text-[13px] leading-snug" data-id="element-480">
            <div className="text-[11px] opacity-60 mb-2" data-id="element-481">USSD • PayShap</div>
            <div className="mt-8 text-center" data-id="element-482">
              <div className="font-bold text-[14px] mb-2" data-id="element-483">
                Processing payment
              </div>
              <div className="opacity-80" data-id="element-484">Talking to your bank...</div>
              <div className="flex justify-center gap-1 mt-3" data-id="element-485">
                <span className="animate-pulse" data-id="element-486">.</span>
                <span className="animate-pulse" style={{
                animationDelay: '0.2s'
              }} data-id="element-487">
                  
                  .
                </span>
                <span className="animate-pulse" style={{
                animationDelay: '0.4s'
              }} data-id="element-488">
                  
                  .
                </span>
              </div>
            </div>
          </div>;
      case 'success':
        return <div className="font-mono text-[13px] leading-snug" data-id="element-489">
            <div className="text-[11px] opacity-60 mb-1" data-id="element-490">USSD</div>
            <div className="font-bold border-b border-black/20 pb-1 mb-2" data-id="element-491">
              ✓ Payment sent
            </div>
            {selectedStore && <>
                <div data-id="element-492">R {selectedStore.balance.toFixed(2)} paid to</div>
                <div className="font-bold" data-id="element-493">{selectedStore.name}</div>
                <div className="mt-2" data-id="element-494">
                  Ref: MS{Math.floor(Math.random() * 90000 + 10000)}
                </div>
              </>}
            <div className="mt-3 opacity-80" data-id="element-495">SMS receipt on its way.</div>
            <div className="mt-2 opacity-80" data-id="element-496">Safe word: SUNRISE</div>
            <div className="mt-3 border-t border-black/20 pt-1 text-[11px] opacity-70" data-id="element-497">
              Press any key to exit
            </div>
          </div>;
      case 'cancelled':
        return <div className="font-mono text-[13px] leading-snug" data-id="element-498">
            <div className="text-[11px] opacity-60 mb-1" data-id="element-499">USSD</div>
            <div className="font-bold border-b border-black/20 pb-1 mb-2" data-id="element-500">
              Cancelled
            </div>
            <div className="opacity-80" data-id="element-501">No payment was made.</div>
            <div className="mt-3 border-t border-black/20 pt-1 text-[11px] opacity-70" data-id="element-502">
              Press any key to exit
            </div>
          </div>;
    }
  };
  // ===== Keypad =====
  const keys: Array<{
    key: string;
    sub?: string;
  }> = [{
    key: '1',
    sub: ''
  }, {
    key: '2',
    sub: 'ABC'
  }, {
    key: '3',
    sub: 'DEF'
  }, {
    key: '4',
    sub: 'GHI'
  }, {
    key: '5',
    sub: 'JKL'
  }, {
    key: '6',
    sub: 'MNO'
  }, {
    key: '7',
    sub: 'PQRS'
  }, {
    key: '8',
    sub: 'TUV'
  }, {
    key: '9',
    sub: 'WXYZ'
  }, {
    key: '*',
    sub: ''
  }, {
    key: '0',
    sub: '+'
  }, {
    key: '#',
    sub: ''
  }];
  return <section id="try-it" className="py-24 bg-ink border-y border-white/5" data-id="element-503">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-504">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-3xl mb-16" data-id="element-505">
          
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-mint-soft border border-mint/20 text-mint text-xs font-medium uppercase tracking-[0.15em] mb-6" data-id="element-506">
            <span className="w-2 h-2 rounded-full bg-mint" data-id="element-507"></span>
            Live customer flow
          </div>
          <h2 className="display text-bone mb-6" style={{
          fontSize: 'clamp(2.25rem, 5vw, 4rem)'
        }} data-id="element-508">
            
            See what your{' '}
            <span className="display-italic text-mint" data-id="element-509">customers</span> actually
            experience.
          </h2>
          <p className="text-lg text-bone-dim leading-relaxed" data-id="element-510">
            This is the real USSD flow your account holders see when they dial
            *120*Masube# and settle their balance. Click through it like they
            would.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-[auto,1fr] gap-12 lg:gap-16 items-center justify-center max-w-5xl mx-auto" data-id="element-511">
          {/* Feature Phone */}
          <div className="flex justify-center" data-id="element-512">
            <div className="w-[300px] bg-[#2A2A2A] rounded-[2.5rem] p-4 border-4 border-[#1A1A1A] shadow-2xl" data-id="element-513">
              {/* Speaker */}
              <div className="w-14 h-1.5 bg-[#1A1A1A] rounded-full mx-auto mt-1 mb-3" data-id="element-514"></div>

              {/* Screen */}
              <div ref={screenRef} className="w-full h-[240px] bg-[#87A96B] rounded-xl p-3 text-black shadow-inner border-4 border-[#1A1A1A] relative overflow-hidden" data-id="element-515">
                
                <AnimatePresence mode="wait" data-id="element-516">
                  <motion.div key={step + (selectedStore?.name || '') + pin.length} initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.15
                }} className="absolute inset-0 p-3" data-id="element-517">
                    
                    {renderScreen()}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Soft Keys + Navigation */}
              <div className="mt-4 px-1" data-id="element-518">
                <div className="flex items-center justify-between mb-2" data-id="element-519">
                  <button onClick={handleCall} aria-label="Call" className="w-12 h-12 rounded-full bg-[#1F4A3A] hover:bg-[#163829] active:scale-95 transition-all flex items-center justify-center shadow-md" data-id="element-520">
                    
                    <Phone className="w-5 h-5 text-white" fill="white" data-id="element-521" />
                  </button>

                  {/* D-Pad */}
                  <div className="relative w-16 h-16 flex items-center justify-center" data-id="element-522">
                    <div className="absolute inset-0 rounded-full bg-[#3A3A3A]" data-id="element-523"></div>
                    <ArrowUp className="absolute top-1 w-4 h-4 text-white/50" data-id="element-524" />
                    <ArrowDown className="absolute bottom-1 w-4 h-4 text-white/50" data-id="element-525" />
                    <div className="relative w-8 h-8 rounded-full bg-[#2A2A2A] border border-white/10 flex items-center justify-center text-white/70 text-[9px] font-bold" data-id="element-526">
                      OK
                    </div>
                  </div>

                  <button onClick={handleEnd} aria-label="End call" className="w-12 h-12 rounded-full bg-[#A8321F] hover:bg-[#8a2818] active:scale-95 transition-all flex items-center justify-center shadow-md" data-id="element-527">
                    
                    <PhoneOff className="w-5 h-5 text-white" fill="white" data-id="element-528" />
                  </button>
                </div>

                {/* Keypad */}
                <div className="grid grid-cols-3 gap-1.5 mt-2" data-id="element-529">
                  {keys.map(({
                  key,
                  sub
                }) => <button key={key} onClick={() => handleKey(key)} className={`relative h-12 rounded-lg bg-[#3A3A3A] hover:bg-[#4A4A4A] active:bg-[#5A5A5A] transition-all border border-white/5 flex flex-col items-center justify-center ${pressedKey === key ? 'bg-[#5A5A5A] scale-95' : ''}`} data-id="element-530">
                    
                      <span className="text-white text-lg font-bold leading-none" data-id="element-531">
                        {key}
                      </span>
                      {sub && <span className="text-white/40 text-[8px] font-medium leading-none mt-0.5" data-id="element-532">
                          {sub}
                        </span>}
                    </button>)}
                </div>
              </div>
            </div>
          </div>

          {/* Guidance Panel */}
          <div className="flex flex-col gap-6" data-id="element-533">
            <motion.div key={step} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="bg-ink-2 border border-white/10 rounded-3xl p-8" data-id="element-534">
              
              <div className="flex items-start gap-4 mb-4" data-id="element-535">
                <div className="w-12 h-12 rounded-2xl bg-mint text-ink flex items-center justify-center flex-shrink-0" data-id="element-536">
                  <Info className="w-6 h-6" data-id="element-537" />
                </div>
                <div data-id="element-538">
                  <div className="text-xs font-semibold text-mint uppercase tracking-[0.2em] mb-1" data-id="element-539">
                    Step guide
                  </div>
                  <h3 className="text-2xl font-semibold text-bone tracking-tight" data-id="element-540">
                    {STEP_HINTS[step].title}
                  </h3>
                </div>
              </div>
              <p className="text-base text-bone-dim leading-relaxed" data-id="element-541">
                {STEP_HINTS[step].body}
              </p>
            </motion.div>

            {/* Progress dots */}
            <div className="bg-ink-2 border border-white/10 rounded-3xl p-6" data-id="element-542">
              <div className="text-xs font-semibold text-muted uppercase tracking-[0.2em] mb-4" data-id="element-543">
                Where you are
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-544">
                {[{
                id: 'idle',
                label: 'Dial'
              }, {
                id: 'menu',
                label: 'Pick store'
              }, {
                id: 'account',
                label: 'Balance'
              }, {
                id: 'confirm',
                label: 'Confirm'
              }, {
                id: 'pin',
                label: 'PIN'
              }, {
                id: 'success',
                label: 'Done'
              }].map((s, i, arr) => {
                const currentIdx = arr.findIndex(x => {
                  if (x.id === 'idle') return step === 'idle' || step === 'dialing' || step === 'connecting';
                  if (x.id === 'success') return step === 'success' || step === 'processing';
                  return step === x.id;
                });
                const isActive = i === currentIdx;
                const isDone = i < currentIdx;
                return <div key={s.id} className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? 'bg-mint text-ink' : isDone ? 'bg-mint-soft text-mint' : 'bg-ink-3 text-muted'}`} data-id="element-545">
                      
                      {isDone && <CheckCircle2 className="w-3.5 h-3.5" data-id="element-546" />}
                      {s.label}
                    </div>;
              })}
              </div>
            </div>

            <button onClick={reset} className="self-start inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ink-3 border border-white/10 text-bone font-medium hover:bg-ink-4 transition-colors" data-id="element-547">
              
              <RotateCcw className="w-5 h-5" data-id="element-548" />
              Start over
            </button>
          </div>
        </div>
      </div>
    </section>;
}